import CSVUploadAndPreviewDialog from "./CSVUploadAndPreviewDialog";

import Seed from "../../utilities/Seed";
import Utility from "../../utilities/Utility";

export default class GlobalHandlersCSV extends CSVUploadAndPreviewDialog {
    /**
     * Dialog for global handlers CSV upload and preview
     * @module GlobalHandlersCSV
     * @param {Object} props React render
     */
    constructor(props) {
        super(props);

        this.getValidatedRow = this.getValidatedRow.bind(this);
        this.onDetectInvalidRows = this.onDetectInvalidRows.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    /**
     * Validates each field on the uploaded CSV file making sure that each row
     * has the proper value based on the provided header.
     * This is called from the parent component.
     * @param {object} row The row to evaluate.
     * @param {string} header The header to evaluate the row against.
     * @returns {object}
     */
    getValidatedRow(row, header) {
        const rowValue = row[header];
        let validated = {};

        switch (header) {
            case "IntentName":
            case "ModuleName":
                if (rowValue === "") {
                    validated = { value: "Cannot be empty", class: "red" };
                } else {
                    const objects = header === "IntentName"
                        ? window.apm.serialized.components.intents
                        : window.apm.serialized.components.modules;
                    const objectIndex = objects.findIndex(object => object.name === rowValue);

                    if (objectIndex === -1) {
                        const uppercaseValue = rowValue.toUpperCase().replace(/ /g, "_");
                        const isUppercaseValid = !!uppercaseValue.match(/^[A-Z][A-Z0-9_]*$/);

                        if (isUppercaseValid) {
                            validated = { value: uppercaseValue, class: "blue" };
                        } else if (!isUppercaseValid) {
                            validated = { value: uppercaseValue, class: "red" };
                        } else {
                            validated = { value: rowValue, class: "black" };
                        }
                    } else {
                        validated = { value: rowValue, class: "black" };
                    }
                }
                break;
            case "IntentDescription":
                if (rowValue === "") {
                    validated = { value: "Cannot be empty", class: "red" };
                } else {
                    validated = { value: rowValue, class: "Black" };
                }
        }

        return validated;
    }

    /**
     * Activated when a row with invalid data is detected.
     * This is called from the parent component.
     */
    onDetectInvalidRows() {
        window.apm.notification.post('error', window.l.notification.fix_csv_errors);

        // Disable the modal submit button (this method is defined on the parent component).
        this.toggleSubmitButtonState(false);
    }

    /**
     * Handler for clicking on the primary action button on the modal.
     * This is called from the parent component.
     */
    onSubmit() {
        const csv = { ...this.state.parsedUploadedFile };
        const intents = window.apm.serialized.components.intents;
        const modules = window.apm.serialized.components.modules;

        csv.data.forEach((row) => {
            const intentName = row['IntentName'] ? row['IntentName'].trim() : '';
            const moduleName = row['ModuleName'] ? row['ModuleName'].trim() : '';
            const intentDescription = row['IntentDescription'] ? row['IntentDescription'].trim() : 'Blank intent';
            const moduleDescription = row['ModuleDescription'] ? row['ModuleDescription'].trim() : 'Blank module';

            // Grab existing module/intent/prompt to make sure to not create duplicates.
            let newIntent = intents.find(x => x.name === intentName) || {};
            let newModule = modules.find(x => x.name === moduleName) || {};

            // Create the new intent if it doesn't exist yet.
            if (Object.keys(newIntent).length === 0) {
                newIntent = Seed.getBlankIntentObject(intentName, intentDescription, true);
                window.apm.addNode(newIntent, false);
            }

            // Create the new module if it doesn't exist yet.
            if (Object.keys(newModule).length === 0) {
                newModule = Seed.getBlankModuleObject(moduleName, moduleDescription);
                Utility.addNodeToTreeDomElement('modules', newModule, 'last');
                window.apm.addNode(newModule, false);
            }

            // Create global intent if it doesn't exist yet
            const currentGlobalIntentIdx = window.apm.serialized.globalIntents.findIndex((x) => x.intent === newIntent.id);
            if (currentGlobalIntentIdx === -1 && newIntent.isGlobalIntent) {
                const globalIntentConditionProperties = Seed.getBlankIntentConditionPropertiesObject(newIntent.id);
                const globalIntentModuleProperties = Seed.getBlankModuleNodeObject(newModule.name, newModule.props);
                delete globalIntentModuleProperties.x;
                delete globalIntentModuleProperties.y;

                window.apm.serialized.globalIntents.push({
                    id: Utility.uuid4(),
                    intent: newIntent.id,
                    module: newModule.id,
                    createdTime: window.apm.timestamp,
                    lastLocalChange: window.apm.timestamp,
                    moduleProperties: globalIntentModuleProperties,
                    conditionProperties: globalIntentConditionProperties,
                });
            }
        });

        // Update tree UI (application panel).
        window.apm.refreshApplicationTree(true);

        // Re-render.
        window.apm.reactDOMComponents.render({
            name: 'IntentsWidget',
            props: { serialized: window.apm.serialized },
            $el: $('#intents'),
        });

        this.setState({
            parsedUploadedFile: '',
            originalUploadedFile: '',
        });

        window.apm.notification.post('success', window.l.notification.import_success);
        w2popup.close();
    }
}
